// src/features/merchantConfig/hooks/useConfigSearch.js
//
// Global search across the LOADED configurations only (per UI prompt §6).
// The search runs synchronously over already-fetched data - it does not
// trigger any network requests. If a category hasn't loaded yet, the search
// UI is responsible for telling the user "Searching N of 12 loaded".
//
// Matching strategy:
//   * Both the field name (humanized) and the stringified scalar value are
//     searched.
//   * Match is case-insensitive substring.
//   * Each match carries enough context to render a click-to-navigate row:
//     category, JSON path, humanized label, value preview, match offsets.

import { useMemo } from "react";
import { humanizeLabel } from "../lib/humanizeLabel";
import { pathToKey } from "../lib/jsonPath";

/**
 * @typedef {Object} ConfigSearchMatch
 * @property {string} categoryKey
 * @property {string} categorySlug
 * @property {string} categoryDisplayName
 * @property {Array<string | number>} path
 * @property {string} pathKey
 * @property {string} labelHumanized
 * @property {string} valuePreview
 * @property {"label" | "value" | "both"} matchedIn
 * @property {Array<{ start: number, end: number }>} labelHighlights
 * @property {Array<{ start: number, end: number }>} valueHighlights
 */

const MAX_RESULTS_PER_CATEGORY = 25;
const VALUE_PREVIEW_MAX_LEN = 120;

/**
 * Walk the value block recursively, yielding match objects whenever the
 * query matches either a humanized field name or a scalar value.
 *
 * @param {unknown} node
 * @param {Array<string | number>} path
 * @param {string} query (lower-cased)
 * @param {{ categoryKey: string, categorySlug: string, categoryDisplayName: string }} categoryMeta
 * @param {ConfigSearchMatch[]} out
 */
function walk(node, path, query, categoryMeta, out) {
  if (out.length >= MAX_RESULTS_PER_CATEGORY) return;
  if (node === null || node === undefined) return;

  if (Array.isArray(node)) {
    for (let i = 0; i < node.length; i += 1) {
      walk(node[i], [...path, i], query, categoryMeta, out);
      if (out.length >= MAX_RESULTS_PER_CATEGORY) return;
    }
    return;
  }

  if (typeof node === "object") {
    for (const fieldKey of Object.keys(node)) {
      const childPath = [...path, fieldKey];
      // For object/array children we still want to test the FIELD NAME
      // itself, then recurse to test the leaf values.
      const child = node[fieldKey];
      const isLeaf =
        child === null ||
        child === undefined ||
        typeof child === "string" ||
        typeof child === "number" ||
        typeof child === "boolean";

      if (isLeaf) {
        const label = humanizeLabel(fieldKey);
        const valueStr = child === null || child === undefined ? "-" : String(child);
        const labelMatches = findAllMatches(label, query);
        const valueMatches = findAllMatches(valueStr, query);

        if (labelMatches.length > 0 || valueMatches.length > 0) {
          /** @type {"label" | "value" | "both"} */
          let matchedIn;
          if (labelMatches.length > 0 && valueMatches.length > 0) matchedIn = "both";
          else if (labelMatches.length > 0) matchedIn = "label";
          else matchedIn = "value";

          out.push({
            categoryKey: categoryMeta.categoryKey,
            categorySlug: categoryMeta.categorySlug,
            categoryDisplayName: categoryMeta.categoryDisplayName,
            path: childPath,
            pathKey: pathToKey(childPath),
            labelHumanized: label,
            valuePreview: truncateForPreview(valueStr),
            matchedIn,
            labelHighlights: labelMatches,
            valueHighlights: valueMatches.map((m) => ({
              start: m.start,
              end: Math.min(m.end, VALUE_PREVIEW_MAX_LEN),
            })),
          });
          if (out.length >= MAX_RESULTS_PER_CATEGORY) return;
        }
      } else {
        // Match on the OBJECT field name itself (so "Business Hours" matches
        // even though its leaves are scalars).
        const label = humanizeLabel(fieldKey);
        const labelMatches = findAllMatches(label, query);
        if (labelMatches.length > 0) {
          out.push({
            categoryKey: categoryMeta.categoryKey,
            categorySlug: categoryMeta.categorySlug,
            categoryDisplayName: categoryMeta.categoryDisplayName,
            path: childPath,
            pathKey: pathToKey(childPath),
            labelHumanized: label,
            valuePreview: Array.isArray(child) ? `${child.length} item(s)` : "Section",
            matchedIn: "label",
            labelHighlights: labelMatches,
            valueHighlights: [],
          });
        }
        walk(child, childPath, query, categoryMeta, out);
      }
      if (out.length >= MAX_RESULTS_PER_CATEGORY) return;
    }
  }
}

function findAllMatches(text, queryLower) {
  if (!text) return [];
  const lower = String(text).toLowerCase();
  const out = [];
  let from = 0;
  while (from <= lower.length - queryLower.length) {
    const found = lower.indexOf(queryLower, from);
    if (found === -1) break;
    out.push({ start: found, end: found + queryLower.length });
    from = found + queryLower.length;
    if (out.length > 5) break; // cap highlight count per token
  }
  return out;
}

function truncateForPreview(str) {
  if (str.length <= VALUE_PREVIEW_MAX_LEN) return str;
  return str.slice(0, VALUE_PREVIEW_MAX_LEN - 1) + "…";
}

/**
 * Group raw matches by category for the dropdown.
 *
 * @param {ConfigSearchMatch[]} matches
 */
function groupByCategory(matches) {
  const groups = new Map();
  for (const m of matches) {
    if (!groups.has(m.categorySlug)) {
      groups.set(m.categorySlug, {
        categoryKey: m.categoryKey,
        categorySlug: m.categorySlug,
        categoryDisplayName: m.categoryDisplayName,
        matches: [],
      });
    }
    groups.get(m.categorySlug).matches.push(m);
  }
  return Array.from(groups.values());
}

/**
 * @param {{
 *   query: string,
 *   results: import('./useAllMerchantConfigs').CategoryProbeResult[],
 *   valueFieldByKey: Record<string, string>,
 * }} options
 */
export function useConfigSearch({ query, results, valueFieldByKey }) {
  const trimmed = (query || "").trim().toLowerCase();
  const enabled = trimmed.length >= 2; // require at least 2 chars to avoid noise

  const { groups, totalMatches } = useMemo(() => {
    if (!enabled) return { groups: [], totalMatches: 0 };

    /** @type {ConfigSearchMatch[]} */
    const flat = [];
    for (const probe of results) {
      if (probe.status !== "provisioned" || !probe.data) continue;
      const valueField = valueFieldByKey[probe.key];
      const valueBlock = valueField ? probe.data[valueField] : null;
      if (!valueBlock || typeof valueBlock !== "object") continue;

      walk(
        valueBlock,
        [],
        trimmed,
        {
          categoryKey: probe.key,
          categorySlug: probe.slug,
          categoryDisplayName: probe.displayName,
        },
        flat
      );
    }
    return { groups: groupByCategory(flat), totalMatches: flat.length };
  }, [enabled, trimmed, results, valueFieldByKey]);

  const loadedCount = results.filter((r) => r.status !== "loading").length;
  const totalCount = results.length;

  return {
    enabled,
    query: trimmed,
    groups,
    totalMatches,
    loadedCount,
    totalCount,
  };
}
